import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Calendar as CalIcon, MapPin, Clock, Users, Info, ExternalLink,
  Phone, MessageCircle, Navigation, Flame, CheckCircle2,
} from "lucide-react";

// ──────────────────────────────────────────────────────────────────────────
// Helpers — pure, side-effect free, easy to unit-test if we ever want to.
// ──────────────────────────────────────────────────────────────────────────

// Strip everything except + and digits so phone deep-links work on iOS
// and Android. Empty string when nothing usable remains.
function cleanPhone(raw) {
  if (!raw) return "";
  return String(raw).replace(/[^\d+]/g, "");
}

// `tel:` is the cross-platform standard; both iOS Safari and Android Chrome
// open the dialer. We keep the URL minimal: no extra args.
function telHref(raw) {
  const p = cleanPhone(raw);
  return p ? `tel:${p}` : "";
}

// wa.me opens WhatsApp app on mobile and Web on desktop. The body is
// optional and pre-fills the chat composer. We keep it short and
// gentle — the operator can edit before sending.
function whatsappHref(raw, msg = "") {
  const p = cleanPhone(raw).replace(/^\+/, "");
  if (!p) return "";
  const q = msg ? `?text=${encodeURIComponent(msg)}` : "";
  return `https://wa.me/${p}${q}`;
}

// Google Maps universal link — works on iOS (opens Maps app via Universal
// Links when installed, browser otherwise), Android (opens Maps app), and
// desktop (opens browser). We URL-encode but DON'T trim the address: a
// full string like "Castello Monaci, Salice Salentino" geocodes better
// than just the city.
function mapsHref(location) {
  if (!location) return "";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
}

// True if the event is within `days` from today. Used to surface a
// "imminente" badge so operators spot urgent matrimoni at a glance.
function isImminent(dateIso, days = 30) {
  if (!dateIso) return false;
  try {
    const [y, m, d] = dateIso.split("-").map(Number);
    if (!y || !m || !d) return false;
    const evt = new Date(y, m - 1, d).getTime();
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diff = (evt - today.getTime()) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= days;
  } catch { return false; }
}

// True when the wedding date is strictly in the past — used to mark
// already-concluded matrimoni in red so the operator can scan the list
// and immediately see "this one is done" vs "this one is coming up".
// Mutually exclusive with `isImminent` (an event can't be both).
function isCompleted(dateIso) {
  if (!dateIso) return false;
  try {
    const [y, m, d] = dateIso.split("-").map(Number);
    if (!y || !m || !d) return false;
    const evt = new Date(y, m - 1, d).getTime();
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return evt < today.getTime();
  } catch { return false; }
}

// Localized month + year for the date stamp on the left edge of each row.
function monthShort(dateIso) {
  if (!dateIso) return "—";
  try {
    const d = new Date(dateIso);
    return d.toLocaleDateString("it-IT", { month: "short" }).replace(".", "");
  } catch { return "—"; }
}
function dayNum(dateIso) {
  if (!dateIso) return "—";
  try { return new Date(dateIso).getDate(); } catch { return "—"; }
}
function yearNum(dateIso) {
  if (!dateIso) return "";
  try { return new Date(dateIso).getFullYear(); } catch { return ""; }
}

// ──────────────────────────────────────────────────────────────────────────
// EventCard — single source of truth for one row.
//
// The card renders TWO different inner layouts depending on the viewport
// (Tailwind `md:` breakpoint = 768px). On mobile we collapse the team
// chips into a single compact line and pin a 3-button action row
// (Chiama · WhatsApp · Maps) under the metadata — the latter is the
// crucial "use with one thumb" affordance demanded by the audit.
// Desktop keeps the original wide-row layout to avoid disrupting the
// existing workflow the user explicitly asked to preserve.
// ──────────────────────────────────────────────────────────────────────────
function EventCard({ e, onCompleted }) {
  // Completed takes precedence over imminent — an event in the past is
  // "done", not "upcoming", regardless of how close it was when imported.
  const completed = isCompleted(e.date) || Boolean(e.event_completed_at);
  const imminent = !completed && isImminent(e.date, 30);
  const [busy, setBusy] = React.useState(false);
  const [marked, setMarked] = React.useState(Boolean(e.event_completed_at));

  // Called by the "Segna completato" button. Delegates to the backend
  // idempotent marker → syncs client's workflow_status to `completed` and
  // stamps completed_at/by + status_history. No prompt shown (spec).
  const doMarkCompleted = async () => {
    if (!e.client_id) return;
    setBusy(true);
    try {
      await api.post(`/clients/${e.client_id}/mark-event-completed`);
      setMarked(true);
      if (onCompleted) onCompleted(e.id);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[events] mark-completed failed", err?.message);
    } finally { setBusy(false); }
  };
  const tel = telHref(e.client_phone);
  const wa = whatsappHref(e.client_phone, `Ciao ${e.client_name || ""}, ti scrivo per il tuo matrimonio.`);
  const maps = mapsHref(e.location);

  // Team summary used by the compact mobile chip-line. Built once per row
  // so the JSX stays readable below.
  const teamChips = [];
  if (e.makeup_artist) teamChips.push({ icon: "💄", ...e.makeup_artist });
  if (e.hair_artist) teamChips.push({ icon: "✂️", ...e.hair_artist });
  (e.assistants || []).forEach((a) => teamChips.push({ icon: "👥", ...a }));

  // Visual treatment for past events: red left border + soft pink wash.
  // Soft enough to keep the row legible at scale (lists of 50+ events).
  const containerClass = completed
    ? "border-b border-border last:border-b-0 bg-rose-50/30 border-l-4 border-l-red-500"
    : "border-b border-border last:border-b-0 bg-white";

  return (
    <div
      className={containerClass}
      data-testid={`event-row-${e.id}`}
      data-event-completed={completed ? "true" : "false"}
    >
      {/* ── Mobile layout (< md). Stacked. ── */}
      <div className="md:hidden p-4 flex flex-col gap-3">
        {/* Top row: date stamp + title + imminent badge */}
        <div className="flex items-start gap-3">
          <div className="text-center w-14 shrink-0">
            <div className="text-[10px] text-neutral-500 uppercase tracking-wider">{monthShort(e.date)}</div>
            <div className="font-serif-heading text-3xl leading-none mt-0.5">{dayNum(e.date)}</div>
            <div className="text-[9px] text-neutral-400 mt-0.5 tabular-nums">{yearNum(e.date)}</div>
          </div>
          <div className="flex-1 min-w-0">
            <Link
              to={`/app/clients/${e.client_id}`}
              className="block"
              data-testid={`event-open-client-${e.id}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-serif-heading text-lg leading-tight truncate">{e.client_name || e.title}</h3>
                {completed && (
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold uppercase tracking-wider"
                    data-testid={`event-completed-${e.id}`}
                    title="Matrimonio già concluso"
                  >
                    <CheckCircle2 className="w-2.5 h-2.5" strokeWidth={2} />
                    Completato
                  </span>
                )}
                {imminent && (
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold uppercase tracking-wider"
                    data-testid={`event-imminent-${e.id}`}
                    title="Matrimonio entro 30 giorni"
                  >
                    <Flame className="w-2.5 h-2.5" strokeWidth={2} />
                    Imminente
                  </span>
                )}
              </div>
              <div className="text-xs text-neutral-500 mt-1 flex flex-wrap gap-x-2.5 gap-y-1">
                {e.time && (
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" strokeWidth={1.5} />
                    {e.time}
                  </span>
                )}
                {e.location && (
                  <span className="inline-flex items-center gap-1 min-w-0">
                    <MapPin className="w-3 h-3 shrink-0" strokeWidth={1.5} />
                    <span className="truncate">{e.location}</span>
                  </span>
                )}
              </div>
            </Link>
          </div>
        </div>

        {/* Team chips line — single horizontal row, scrollable if many */}
        {teamChips.length > 0 ? (
          <div className="flex items-center gap-1.5 overflow-x-auto -mx-1 px-1 pb-0.5">
            {teamChips.slice(0, 4).map((t, i) => (
              <span
                key={`${t.id}-${i}`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-neutral-50 border border-border text-[11px] shrink-0"
              >
                <span aria-hidden="true">{t.icon}</span>
                <span
                  className="w-1.5 h-1.5 rounded-full inline-block"
                  style={{ backgroundColor: t.color || "#94A3B8" }}
                />
                <span className="truncate max-w-[80px]">{t.name}</span>
              </span>
            ))}
            {teamChips.length > 4 && (
              <span className="text-[11px] text-neutral-500 shrink-0 pl-1">
                +{teamChips.length - 4}
              </span>
            )}
          </div>
        ) : (
          <div className="text-[11px] text-neutral-400 italic" data-testid={`event-no-artist-${e.id}`}>
            Nessun operatore assegnato
          </div>
        )}

        {/* Action row — the crown jewel of the mobile redesign.
            44px min-height (Apple HIG touch target), full-width 3-col
            grid so a thumb on the right side of the phone can reach
            any button without re-grip. Buttons are AS LARGE as the
            card allows; the icons are oversized for one-handed use. */}
        <div className="grid grid-cols-3 gap-2" data-testid={`event-actions-${e.id}`}>
          <a
            href={tel || undefined}
            onClick={(ev) => { if (!tel) { ev.preventDefault(); } }}
            className={`flex items-center justify-center gap-1.5 h-11 rounded-md border transition-all ${
              tel
                ? "bg-emerald-50 border-emerald-200 text-emerald-800 active:bg-emerald-100"
                : "bg-neutral-50 border-neutral-200 text-neutral-300 cursor-not-allowed"
            }`}
            data-testid={`event-action-call-${e.id}`}
            aria-disabled={!tel}
          >
            <Phone className="w-4 h-4" strokeWidth={1.7} />
            <span className="text-[12.5px] font-medium">Chiama</span>
          </a>
          <a
            href={wa || undefined}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(ev) => { if (!wa) { ev.preventDefault(); } }}
            className={`flex items-center justify-center gap-1.5 h-11 rounded-md border transition-all ${
              wa
                ? "bg-green-50 border-green-200 text-green-800 active:bg-green-100"
                : "bg-neutral-50 border-neutral-200 text-neutral-300 cursor-not-allowed"
            }`}
            data-testid={`event-action-whatsapp-${e.id}`}
            aria-disabled={!wa}
          >
            <MessageCircle className="w-4 h-4" strokeWidth={1.7} />
            <span className="text-[12.5px] font-medium">WhatsApp</span>
          </a>
          <a
            href={maps || undefined}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(ev) => { if (!maps) { ev.preventDefault(); } }}
            className={`flex items-center justify-center gap-1.5 h-11 rounded-md border transition-all ${
              maps
                ? "bg-blue-50 border-blue-200 text-blue-800 active:bg-blue-100"
                : "bg-neutral-50 border-neutral-200 text-neutral-300 cursor-not-allowed"
            }`}
            data-testid={`event-action-maps-${e.id}`}
            aria-disabled={!maps}
          >
            <Navigation className="w-4 h-4" strokeWidth={1.7} />
            <span className="text-[12.5px] font-medium">Maps</span>
          </a>
        </div>
        {/* Segna completato — only for real wedding events (type==='wedding')
            that actually happened (date < today). Idempotent — button
            disables itself once the client is flagged. */}
        {e.client_id && e.type === "wedding" && completed && !marked && (
          <button
            type="button"
            onClick={doMarkCompleted}
            disabled={busy}
            className="mt-3 w-full h-10 rounded-md border border-red-300 bg-red-50 text-red-700 font-medium text-[13px] flex items-center justify-center gap-1.5 disabled:opacity-60"
            data-testid={`event-mark-completed-${e.id}`}
          >
            <CheckCircle2 className="w-4 h-4" strokeWidth={1.7} />
            {busy ? "Salvataggio…" : "Segna evento come completato"}
          </button>
        )}
        {e.client_id && e.type === "wedding" && marked && (
          <div className="mt-3 w-full h-10 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 font-medium text-[12px] flex items-center justify-center gap-1.5" data-testid={`event-completed-marker-${e.id}`}>
            <CheckCircle2 className="w-4 h-4" strokeWidth={1.7} />
            Pratica cliente aggiornata a TERMINATA
          </div>
        )}
      </div>

      {/* ── Desktop layout (md+). Unchanged from v9.36 — single wide row,
          existing CTA to the right. We keep this branch completely
          isolated so any future mobile tweak can't regress the desktop
          experience the user explicitly chose to preserve. ── */}
      <div className="hidden md:flex px-5 py-4 items-center gap-4">
        <div className="text-center w-16 shrink-0">
          <div className="text-xs text-neutral-500 uppercase">{monthShort(e.date)}</div>
          <div className="font-serif-heading text-2xl leading-none mt-0.5">{dayNum(e.date)}</div>
          <div className="text-[10px] text-neutral-400 mt-0.5 tabular-nums">{yearNum(e.date)}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium flex items-center gap-2 flex-wrap">
            <span>{e.title}</span>
            {(e.makeup_artist || e.hair_artist || (e.assistants && e.assistants.length > 0)) && (
              <span className="inline-flex items-center gap-2 text-xs font-normal text-neutral-600 flex-wrap" data-testid={`event-artists-${e.id}`}>
                {e.makeup_artist && (
                  <span className="inline-flex items-center gap-1">
                    <span className="text-sm leading-none" aria-label="Trucco">💄</span>
                    <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: e.makeup_artist.color || "#94A3B8" }} aria-hidden="true" />
                    {e.makeup_artist.name}
                  </span>
                )}
                {e.makeup_artist && e.hair_artist && <span className="text-neutral-300">·</span>}
                {e.hair_artist && (
                  <span className="inline-flex items-center gap-1">
                    <span className="text-sm leading-none" aria-label="Capelli">✂️</span>
                    <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: e.hair_artist.color || "#94A3B8" }} aria-hidden="true" />
                    {e.hair_artist.name}
                  </span>
                )}
                {e.assistants && e.assistants.length > 0 && (
                  <>
                    {(e.makeup_artist || e.hair_artist) && <span className="text-neutral-300">·</span>}
                    <span
                      className="inline-flex items-center gap-1"
                      title={e.assistants.map((a) => a.name).join(", ")}
                      data-testid={`event-assistants-${e.id}`}
                    >
                      <span className="text-sm leading-none" aria-label="Assistenti">👥</span>
                      <span className="inline-flex items-center gap-0.5">
                        {e.assistants.slice(0, 3).map((a) => (
                          <span key={a.id} className="w-2 h-2 rounded-full inline-block ring-1 ring-white" style={{ backgroundColor: a.color || "#94A3B8" }} />
                        ))}
                      </span>
                      {e.assistants.length === 1
                        ? e.assistants[0].name
                        : `${e.assistants[0].name} +${e.assistants.length - 1}`}
                    </span>
                  </>
                )}
              </span>
            )}
            {!e.makeup_artist && !e.hair_artist && (!e.assistants || e.assistants.length === 0) && (
              <span className="text-xs font-normal text-neutral-400" data-testid={`event-no-artist-${e.id}-d`}>
                Non assegnato
              </span>
            )}
          </div>
          <div className="text-xs text-neutral-500 flex flex-wrap gap-3 mt-1">
            {e.client_name && <span>{e.client_name}</span>}
            {e.time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" strokeWidth={1.5} />{e.time}</span>}
            {e.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" strokeWidth={1.5} />{e.location}</span>}
            <span className="label-uppercase text-primary">{e.type}</span>
            {completed && (
              <span
                className="inline-flex items-center gap-1 text-red-700 font-semibold uppercase tracking-wider"
                data-testid={`event-completed-${e.id}-desktop`}
              >
                <CheckCircle2 className="w-3 h-3" strokeWidth={2} /> Completato
              </span>
            )}
            {imminent && (
              <span className="inline-flex items-center gap-1 text-rose-700 font-semibold uppercase tracking-wider">
                <Flame className="w-3 h-3" strokeWidth={1.7} /> Imminente
              </span>
            )}
          </div>
        </div>
        <Link
          to={`/app/clients/${e.client_id}`}
          className="text-primary hover:underline text-xs inline-flex items-center gap-1 shrink-0"
          data-testid={`event-open-client-${e.id}-desktop`}
          title="Apri scheda cliente"
        >
          <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span>Apri</span>
        </Link>
        {/* Desktop: same "Segna evento come completato" affordance as mobile.
            Position: end of the row (after the "Apri" link) so past-events
            get a visible CTA without expanding the row height when the
            state doesn't apply. */}
        {e.client_id && e.type === "wedding" && completed && !marked && (
          <button
            type="button"
            onClick={doMarkCompleted}
            disabled={busy}
            className="ml-2 h-7 px-2 rounded border border-red-300 bg-red-50 text-red-700 text-[11px] font-semibold uppercase tracking-wider inline-flex items-center gap-1 disabled:opacity-60 shrink-0"
            data-testid={`event-mark-completed-${e.id}-desktop`}
          >
            <CheckCircle2 className="w-3 h-3" strokeWidth={2} />
            {busy ? "…" : "Segna completato"}
          </button>
        )}
        {e.client_id && e.type === "wedding" && marked && (
          <span
            className="ml-2 h-7 px-2 rounded border border-emerald-200 bg-emerald-50 text-emerald-700 text-[11px] font-semibold uppercase tracking-wider inline-flex items-center gap-1 shrink-0"
            data-testid={`event-completed-marker-${e.id}-desktop`}
            title="Pratica cliente aggiornata a TERMINATA"
          >
            <CheckCircle2 className="w-3 h-3" strokeWidth={2} />
            Terminato
          </span>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Events page = read-only list of clients whose contract is "firmato".
// No manual creation: the source of truth is the signed contract on each
// client. This page intentionally COEXISTS with Agenda — the audit
// confirmed they serve different needs:
//   • Agenda = temporal/calendar planning (states, week/day grids)
//   • Eventi = sorted list of weddings with operator filter
// ──────────────────────────────────────────────────────────────────────────
export default function Events() {
  const [items, setItems] = useState([]);
  const [team, setTeam] = useState([]);
  const [operatorFilter, setOperatorFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [evRes, tmRes] = await Promise.allSettled([
          api.get("/events"),
          api.get("/team"),
        ]);
        if (cancelled) return;
        if (evRes.status === "fulfilled") setItems(evRes.value.data || []);
        if (tmRes.status === "fulfilled") setTeam(tmRes.value.data || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // Operator filter (frontend-side, includes assistants)
  const filteredItems = useMemo(() => (operatorFilter === "all"
    ? items
    : items.filter((e) =>
        (e.makeup_artist && e.makeup_artist.id === operatorFilter) ||
        (e.hair_artist && e.hair_artist.id === operatorFilter) ||
        (e.assistants && e.assistants.some((a) => a.id === operatorFilter)),
      )
  ), [items, operatorFilter]);

  return (
    <div className="space-y-6" data-testid="events-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="label-uppercase">Agenda</span>
          <h1 className="font-serif-heading text-4xl sm:text-5xl tracking-tight mt-2">Eventi</h1>
          <p className="text-sm text-neutral-600 mt-2">
            Lista automatica dei matrimoni dai contratti firmati.
          </p>
        </div>
      </div>

      {/* Info banner — hidden on mobile to save vertical space. Operators
          on the phone already know the rules; on desktop the banner is a
          useful pointer for new admins. */}
      <div
        className="hidden md:flex bg-secondary/30 border border-border rounded-sm px-4 py-3 items-start gap-3"
        data-testid="events-auto-banner"
      >
        <Info className="w-4 h-4 text-primary mt-0.5 shrink-0" strokeWidth={1.5} />
        <p className="text-sm text-neutral-700">
          Gli eventi vengono <strong>generati automaticamente</strong> dai clienti con contratto <strong>firmato</strong>
          {" "}e data matrimonio impostata. Per aggiungere un nuovo evento,{" "}
          <Link to="/app/clients" className="text-primary hover:underline font-medium">
            crea o aggiorna un cliente
          </Link>{" "}
          e firma il contratto.
        </p>
      </div>

      {/* Operator filter — kept identical on desktop, made more compact and
          horizontally scrollable on mobile so the pills don't push the
          list below the fold on phones with 6+ operators. */}
      {team.length > 0 && (
        <div
          className="bg-white border border-border rounded-sm p-3 md:p-4"
          data-testid="events-operator-filter"
        >
          <div className="flex items-center gap-2 mb-2 md:hidden">
            <Users className="w-3.5 h-3.5 text-primary" strokeWidth={1.5} />
            <span className="label-uppercase">Operatori</span>
          </div>
          <div className="hidden md:flex items-start gap-3 flex-wrap">
            <div className="flex items-center gap-2 shrink-0 mt-1">
              <Users className="w-4 h-4 text-primary" strokeWidth={1.5} />
              <span className="label-uppercase">Operatori</span>
            </div>
            <div className="flex flex-wrap gap-2 flex-1">
              <OperatorPill testid="events-operator-pill-all" active={operatorFilter === "all"} onClick={() => setOperatorFilter("all")}>
                Tutti
              </OperatorPill>
              {team.map((m) => (
                <OperatorPill
                  key={m.id}
                  testid={`events-operator-pill-${m.id}`}
                  active={operatorFilter === m.id}
                  color={m.color}
                  onClick={() => setOperatorFilter(m.id)}
                >
                  {m.name}
                </OperatorPill>
              ))}
            </div>
          </div>
          {/* Mobile: horizontal scroll strip so all operators fit on a
              single row even when there are 8+. The `-mx-3` extends the
              scroll area to the card edges, matching iOS Mail style. */}
          <div className="md:hidden flex gap-1.5 overflow-x-auto -mx-3 px-3 pb-0.5">
            <OperatorPill testid="events-operator-pill-all-m" active={operatorFilter === "all"} onClick={() => setOperatorFilter("all")} compact>
              Tutti
            </OperatorPill>
            {team.map((m) => (
              <OperatorPill
                key={m.id}
                testid={`events-operator-pill-${m.id}-m`}
                active={operatorFilter === m.id}
                color={m.color}
                onClick={() => setOperatorFilter(m.id)}
                compact
              >
                {m.name}
              </OperatorPill>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="bg-white border border-border rounded-sm p-16 text-center text-sm text-neutral-500">
          Caricamento…
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-white border border-border rounded-sm p-10 md:p-16 text-center" data-testid="events-empty">
          <CalIcon className="w-10 h-10 mx-auto text-primary" strokeWidth={1.5} />
          <h3 className="font-serif-heading text-2xl mt-4">
            {items.length === 0 ? "Nessun evento" : "Nessun evento per questo operatore"}
          </h3>
          <p className="text-neutral-600 mt-2 text-sm">
            {items.length === 0
              ? "Gli eventi appariranno qui non appena un cliente avrà il contratto firmato."
              : "Cambia filtro o seleziona \"Tutti\" per vedere tutti gli eventi."}
          </p>
        </div>
      ) : (
        <div
          className="bg-white border border-border rounded-sm overflow-hidden"
          data-testid="events-list"
        >
          {filteredItems.map((e) => <EventCard key={e.id} e={e} />)}
        </div>
      )}
    </div>
  );
}

// Small presentational helper to keep the JSX above readable. Same pill
// styling for desktop and mobile, just a `compact` variant that nudges
// padding down so 6+ operators fit on a 375px-wide screen.
function OperatorPill({ children, active, onClick, color, testid, compact = false }) {
  const base = compact
    ? "px-2.5 py-1 rounded-full border text-[11px] font-medium shrink-0"
    : "px-3 py-1.5 rounded-full border text-xs font-medium transition-all";
  const tone = active
    ? "bg-primary text-primary-foreground border-primary"
    : "bg-white border-border text-neutral-700 hover:border-primary hover:text-primary";
  return (
    <button type="button" onClick={onClick} className={`${base} ${tone} inline-flex items-center gap-1.5`} data-testid={testid}>
      {color && (
        <span
          className="w-2 h-2 rounded-full inline-block"
          style={{ backgroundColor: color || "#94A3B8" }}
        />
      )}
      {children}
    </button>
  );
}
